import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { apiService, getUserPreference } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';

/**
 * Sidebar theme (rail color) store.
 *
 * Source of truth is the backend `UserPreference.sidebar_theme` column, read
 * and written via `/user-preference` (GET returns it; PUT `{ preset, rail_color }`
 * saves it). localStorage is kept as an optimistic cache so the rail paints
 * instantly on load and survives offline; on sign-in we hydrate from the server
 * (which wins), and every change is persisted back to the user's account so the
 * theme follows them across devices.
 */

export interface SidebarTheme {
  /** Preset id from SIDEBAR_PRESETS, or null for a custom color. */
  preset: string | null;
  railColor: string;
}

/** Backend wire shape under `sidebar_theme`. */
interface SidebarThemeWire {
  preset: string | null;
  rail_color: string;
}

const ENDPOINT = '/user-preference';
const STORAGE_KEY = 'clarix-sidebar-theme';
const DEFAULT_THEME: SidebarTheme = { preset: 'default', railColor: '#FFFFFF' };

function readTheme(): SidebarTheme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed = JSON.parse(raw) as Partial<SidebarTheme>;
    if (typeof parsed.railColor !== 'string') return DEFAULT_THEME;
    return { preset: typeof parsed.preset === 'string' ? parsed.preset : null, railColor: parsed.railColor };
  } catch {
    return DEFAULT_THEME;
  }
}

let theme: SidebarTheme = readTheme();
const listeners = new Set<() => void>();

/** Hydration bookkeeping: which user we've fetched for, and whether the user
 *  changed the theme this session (so a late GET doesn't clobber a fresh edit). */
let hydratedForUser: string | null = null;
let userTouched = false;

function cacheAndNotify(next: SidebarTheme) {
  theme = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private-mode failures */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Pull the saved theme from the user's account; server value wins over the
 *  local cache unless the user has already changed it this session. */
async function hydrateFromServer(userKey: string) {
  if (hydratedForUser === userKey) return;
  hydratedForUser = userKey;
  userTouched = false;
  try {
    const pref = await getUserPreference<{ sidebar_theme: SidebarThemeWire | null }>();
    const wire = pref?.sidebar_theme;
    if (!userTouched && wire && typeof wire.rail_color === 'string') {
      cacheAndNotify({ preset: wire.preset ?? null, railColor: wire.rail_color });
    }
  } catch {
    /* offline / not ready — keep the local cache */
  }
}

/** Persist the theme to the user's account (fire-and-forget; the optimistic
 *  local update has already been applied). */
function persistToServer(next: SidebarTheme) {
  apiService
    .put(ENDPOINT, { preset: next.preset, rail_color: next.railColor })
    .catch(() => {
      /* keep the optimistic local value; next change/login will reconcile */
    });
}

export function useSidebarTheme() {
  const current = useSyncExternalStore(
    subscribe,
    () => theme,
    () => theme,
  );

  // Hydrate from the backend once we know who the user is (and re-hydrate on
  // account switch). Gated on auth so we never call the endpoint while signed out.
  const userKey = useAuthStore((s) => s.user?.email ?? null);
  useEffect(() => {
    if (userKey) void hydrateFromServer(userKey);
  }, [userKey]);

  const setTheme = useCallback((next: SidebarTheme) => {
    userTouched = true;
    cacheAndNotify(next);
    persistToServer(next);
  }, []);

  const resetTheme = useCallback(() => {
    userTouched = true;
    cacheAndNotify(DEFAULT_THEME);
    persistToServer(DEFAULT_THEME);
  }, []);

  return { theme: current, setTheme, resetTheme };
}
